/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Container,
    HStack,
    IconButton,
    useDisclosure,
    Text,
} from '@chakra-ui/react';
import { MdPlaylistPlay } from 'react-icons/md';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';
import Tooltip from 'components/v4/Tooltip';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import RefireModal from '../new/modals/RefireModal.jsx';
import useGetTriggerByID from '../new/queries/useGetTriggerByID.jsx';
import useRefireTrigger from '../new/queries/useRefireTrigger.jsx';

const History = ({ triggerId, resetToMainTable }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const gridRef = useRef();
    const isMountedRef = useRef(true);
    const hasResetBeenCalledRef = useRef(false);

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const [triggerData, setTriggerData] = useState(null);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [isComponentActive, setIsComponentActive] = useState(true);

    const {
        isOpen: isRefireOpen,
        onOpen: onRefireOpen,
        onClose: onRefireClose,
    } = useDisclosure();

    // Track when resetToMainTable is called and prevent ALL subsequent calls
    const wrappedResetToMainTable = useCallback(
        (...args) => {
            if (hasResetBeenCalledRef.current) {
                return;
            }

            hasResetBeenCalledRef.current = true;
            setIsComponentActive(false);
            isMountedRef.current = false;

            if (resetToMainTable) {
                resetToMainTable(...args);
            }
        },
        [resetToMainTable],
    );

    const shouldExecuteQuery =
        isComponentActive &&
        !hasResetBeenCalledRef.current &&
        Boolean(triggerId) &&
        triggerId !== null &&
        triggerId !== undefined;

    const { isLoading, refetch } = useGetTriggerByID(
        shouldExecuteQuery ? triggerId : null,
        shouldExecuteQuery ? setTriggerData : () => {},
        shouldExecuteQuery ? wrappedResetToMainTable : () => {},
        shouldExecuteQuery,
        false,
        null,
    );

    const { mutate: refireTrigger, isPending: isRefiring } = useRefireTrigger(
        () => {
            if (hasResetBeenCalledRef.current) return;
            onRefireClose();
            if (shouldExecuteQuery) {
                refetch();
            }
        },
    );

    const handleRefire = useCallback(
        (params) => {
            if (hasResetBeenCalledRef.current) return;
            setSelectedHistory(params.data);
            onRefireOpen();
        },
        [onRefireOpen],
    );

    const confirmRefire = useCallback(() => {
        if (!selectedHistory || hasResetBeenCalledRef.current) return;
        refireTrigger(selectedHistory.id);
    }, [selectedHistory, refireTrigger]);

    useEffect(() => {
        return () => {
            hasResetBeenCalledRef.current = true;
            isMountedRef.current = false;
            setIsComponentActive(false);
        };
    }, []);

    useEffect(() => {
        if (!triggerId) {
            hasResetBeenCalledRef.current = true;
            setIsComponentActive(false);
        }
    }, [triggerId]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!shouldExecuteQuery) {
                hasResetBeenCalledRef.current = true;
                setIsComponentActive(false);
            }
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [resetToMainTable, shouldExecuteQuery]);

    const colDefs = useMemo(
        () => [
            {
                maxWidth: 70,
                width: 70,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellRenderer: 'agGroupCellRenderer',
                lockPosition: 'left',
            },
            {
                field: 'triggeredAt',
                headerName: 'Triggered At',
                flex: 1,
                minWidth: 200,
                cellRenderer: TimeCellRenderer,
                sort: 'desc',
            },
            {
                headerName: 'Actions',
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                lockPosition: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    return (
                        <HStack
                            spacing={1}
                            justifyContent="left"
                            alignItems="left"
                            width="100%"
                            height="100%">
                            <Tooltip label="Refire Trigger">
                                <IconButton
                                    icon={
                                        <MdPlaylistPlay
                                            fill={darkMode ? 'white' : 'black'}
                                            size="30px"
                                        />
                                    }
                                    aria-label="Refire"
                                    variant="ghost"
                                    size="md"
                                    onClick={() => handleRefire(params)}
                                />
                            </Tooltip>
                        </HStack>
                    );
                },
            },
        ],
        [darkMode, handleRefire],
    );

    const defaultCellRendererColDef = useMemo(() => {
        return {
            sortable: false,
            filter: false,
            resizable: true,
            flex: 1,
            minWidth: 100,
            suppressHeaderMenuButton: true,
            cellRenderer: CopyCellRenderer,
        };
    }, []);

    const detailCellRendererParams = useMemo(
        () => ({
            detailGridOptions: {
                pagination: false,
                columnDefs: [
                    {
                        headerName: 'Parameter Key',
                        field: 'key',
                        flex: 1,
                        minWidth: 200,
                        cellRenderer: CopyCellRenderer,
                    },
                    {
                        headerName: 'Parameter Value',
                        field: 'value',
                        flex: 2,
                        minWidth: 300,
                        cellRenderer: CopyCellRenderer,
                    },
                ],
                headerHeight: 38,
                defaultColDef: defaultCellRendererColDef,
            },
            getDetailRowData: (params) => {
                const parameters = params.data.parameters || {};
                const paramEntries = Object.entries(parameters);

                if (paramEntries.length === 0) {
                    params.successCallback([]);
                    return;
                }

                const parameterRows = paramEntries
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => ({
                        key,
                        value: String(value),
                    }));

                params.successCallback(parameterRows);
            },
        }),
        [defaultCellRendererColDef],
    );

    useEffect(() => {
        if (
            triggerData?.triggerHistory &&
            isMountedRef.current &&
            !hasResetBeenCalledRef.current &&
            shouldExecuteQuery
        ) {
            setTableData({
                columns: colDefs,
                data: triggerData.triggerHistory,
                detailCellRendererParams: detailCellRendererParams,
            });
        }
    }, [triggerData, colDefs, detailCellRendererParams, shouldExecuteQuery]);

    if (
        !isComponentActive ||
        hasResetBeenCalledRef.current ||
        !shouldExecuteQuery
    ) {
        return null;
    }

    return (
        <>
            <Container
                as="div"
                maxW="container.xl"
                px={0}
                aria-label="trigger history table">
                <Text
                    sx={{
                        fontSize: '18',
                        fontWeight: '500',
                        marginBottom: '20px',
                    }}>
                    Trigger History
                </Text>
                <AGGridMain
                    gridRef={gridRef}
                    tableData={tableData}
                    isLoading={isLoading}
                    refetch={refetch}
                    overlayNoRowsTemplate="No History Found"
                    titleName="Trigger History"
                    masterDetail={true}
                />
            </Container>

            <RefireModal
                isOpen={isRefireOpen}
                onClose={onRefireClose}
                selectedHistory={selectedHistory}
                isRefiring={isRefiring}
                onConfirm={confirmRefire}
            />
        </>
    );
};

export default History;
